import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";

export interface SelectiveReturnItem {
    itemCode: string;
    returnQuantity: number;
    selected: boolean;
}

export interface NewSaleItem {
    itemCode?: string | null;
    itemName?: string | null;
    quantity: number;
    unitPrice: number;
    originalPrice?: number | null;
    discount?: number;
    discountAmount?: number;
    amount: number;
}

export interface SelectiveReturnPayment {
    paymode: string;
    amount: number;
    cardTypeCode?: string;
    cardStationNo?: string;
    cardApprovalCode?: string;
    cardLastDigits?: string;
}

export interface SelectiveReturnPromotion {
    promoId: number;
    promoCode?: string | null;
    promoName?: string | null;
    promoDisper?: number | null;
    promoDisamt?: number | null;
    promoFreeItem?: string | null;
    promoFreeVoucher?: string | null;
    promoDiscountAmount?: number | null;
}

export interface SelectiveReturnRequest {
    originalInvoiceNo: string;
    customerId?: number | null;
    customerCode?: string | null;
    customerName?: string | null;
    cashierId?: string | null;
    items: SelectiveReturnItem[];
    newItems?: NewSaleItem[];
    returnReason?: string | null;
    paymode?: string | null;
    paymentReceived?: number;
    billDiscountPercentage?: number;
    billDiscountAmount?: number;
    payments?: SelectiveReturnPayment[];
    promotions?: SelectiveReturnPromotion[];
}

export interface DirectReturnItem {
    itemCode: string;
    itemName: string;
    returnQuantity: number;
    unitPrice: number;
    discount?: number;
}

export interface DirectReturnRequest {
    customerId?: number | null;
    customerCode?: string | null;
    customerName?: string | null;
    cashierId?: string | null;
    items: DirectReturnItem[];
    returnReason?: string | null;
    paymode?: string | null;
}

export interface SelectiveReturnResponse {
    return_invoice_no: string;
    original_invoice_no: string;
    item_count: number;
    return_amount: number;
    customer_name?: string | null;
    created_at?: string | null;
    status?: string;
    message?: string;
}

export interface DirectReturnResponse {
    return_invoice_no: string;
    item_count: number;
    return_amount: number;
    customer_name?: string | null;
    created_at?: string | null;
    status?: string;
    message?: string;
}

/**
 * Process selective return with item selection
 * Only selected items are converted to return rows
 */
export const processSelectiveReturn = async (payload: SelectiveReturnRequest): Promise<SelectiveReturnResponse> => {
    try {
        const response: ApiResponse<SelectiveReturnResponse | null> = (
            await apiClient.post("/pos_returns/selective", {
                original_invoice_no: payload.originalInvoiceNo,
                customer_id: payload.customerId ?? null,
                customer_code: payload.customerCode ?? null,
                customer_name: payload.customerName ?? null,
                cashier_id: payload.cashierId ?? null,
                items: payload.items.map((item) => ({
                    item_code: item.itemCode,
                    return_quantity: item.returnQuantity,
                    selected: item.selected,
                })),
                new_items: (payload.newItems ?? []).map((item) => ({
                    item_code: item.itemCode ?? null,
                    item_name: item.itemName ?? null,
                    quantity: item.quantity,
                    unit_price: item.unitPrice,
                    original_price: item.originalPrice ?? null,
                    discount: item.discount ?? 0,
                    discount_amount: item.discountAmount ?? 0,
                    amount: item.amount,
                })),
                return_reason: payload.returnReason ?? null,
                paymode: payload.paymode ?? "CSH",
                payment_received: payload.paymentReceived ?? 0,
                bill_discount_percentage: payload.billDiscountPercentage ?? 0,
                bill_discount_amount: payload.billDiscountAmount ?? 0,
                payments: (payload.payments ?? []).map((paymentEntry) => ({
                    paymode: paymentEntry.paymode,
                    amount: paymentEntry.amount,
                    card_type_code: paymentEntry.cardTypeCode,
                    card_station_no: paymentEntry.cardStationNo,
                    card_approval_code: paymentEntry.cardApprovalCode,
                    card_last_digits: paymentEntry.cardLastDigits,
                })),
                promotions: (payload.promotions ?? []).map((promo) => ({
                    promo_id: promo.promoId,
                    promo_code: promo.promoCode ?? null,
                    promo_name: promo.promoName ?? null,
                    promo_disper: promo.promoDisper ?? null,
                    promo_disamt: promo.promoDisamt ?? null,
                    promo_free_item: promo.promoFreeItem ?? null,
                    promo_free_voucher: promo.promoFreeVoucher ?? null,
                    promo_discount_amount: promo.promoDiscountAmount ?? null,
                })),
            }, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        if (!response.data) {
            throw new Error("Selective return request completed without a response payload.");
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            const detail = error.response.data.detail;
            const message = typeof detail === "string" ? detail : JSON.stringify(detail);
            throw new Error(message);
        }

        throw new Error("Failed to process selective return");
    }
};

/**
 * Process direct (non-referenced) return
 * Users return items directly without invoice reference
 */
export const processDirectReturn = async (payload: DirectReturnRequest): Promise<DirectReturnResponse> => {
    try {
        const response: ApiResponse<DirectReturnResponse | null> = (
            await apiClient.post("/pos_returns/direct", {
                customer_id: payload.customerId ?? null,
                customer_code: payload.customerCode ?? null,
                customer_name: payload.customerName ?? null,
                cashier_id: payload.cashierId ?? null,
                items: payload.items.map((item) => ({
                    item_code: item.itemCode,
                    item_name: item.itemName,
                    return_quantity: item.returnQuantity,
                    unit_price: item.unitPrice,
                    discount: item.discount ?? 0,
                })),
                return_reason: payload.returnReason ?? null,
                paymode: payload.paymode ?? "CSH",
            }, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        if (!response.data) {
            throw new Error("Direct return request completed without a response payload.");
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            const detail = error.response.data.detail;
            const message = typeof detail === "string" ? detail : JSON.stringify(detail);
            throw new Error(message);
        }

        throw new Error("Failed to process direct return");
    }
};
